import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent } from '@/components/ui';
import { useToast } from '@/components/ui';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ───────────────────────────────────────────────────────────────────

interface PendingEntry {
  id: string;
  userId: string;
  userName: string;
  jobId: string;
  jobTitle: string;
  clientName: string;
  practiceEntityName: string;
  entryDate: string;
  durationMinutes: number;
  description: string | null;
  isBillable: boolean;
  approvalStatus: string;
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

function usePendingTimeEntries() {
  return useQuery({
    queryKey: ['time-entries', 'pending-approval'],
    queryFn: async () => {
      const response = await apiClient.get<{
        success: boolean;
        data: Array<Record<string, unknown>>;
        pagination: { total: number };
      }>('/time-entries?approval_status=pending&pageSize=100');

      return (response.data.data ?? []).map((e) => ({
        id: e.id as string,
        userId: (e.user_id || '') as string,
        userName: (e.user_name || e.staff_name || 'Unknown') as string,
        jobId: (e.job_id || '') as string,
        jobTitle: (e.job_title || '') as string,
        clientName: (e.client_name || '') as string,
        practiceEntityName: (e.practice_entity_name || '') as string,
        entryDate: (e.entry_date || '') as string,
        durationMinutes: (e.duration_minutes || 0) as number,
        description: (e.description || null) as string | null,
        isBillable: (e.is_billable ?? true) as boolean,
        approvalStatus: (e.approval_status || 'pending') as string,
      }));
    },
  });
}

function useApproveEntries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (timeEntryIds: string[]) => {
      const response = await apiClient.post<ApiResponse<unknown>>('/time-entries/approve', {
        time_entry_ids: timeEntryIds,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });
}

function useRejectEntries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ timeEntryIds, comment }: { timeEntryIds: string[]; comment: string }) => {
      const response = await apiClient.post<ApiResponse<unknown>>('/time-entries/reject', {
        time_entry_ids: timeEntryIds,
        rejection_comment: comment,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDuration(minutes: number): string {
  if (minutes === 0) return '0m';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// ─── Accordion Card ──────────────────────────────────────────────────────────

interface EmployeeAccordionProps {
  userName: string;
  entries: PendingEntry[];
  totalMinutes: number;
  billableMinutes: number;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectUser: (entryIds: string[]) => void;
}

function EmployeeAccordion({
  userName,
  entries,
  totalMinutes,
  billableMinutes,
  selectedIds,
  onToggleSelect,
  onToggleSelectUser,
}: EmployeeAccordionProps) {
  const [expanded, setExpanded] = useState(false);

  const allUserSelected = entries.every((e) => selectedIds.has(e.id));
  const someUserSelected = entries.some((e) => selectedIds.has(e.id)) && !allUserSelected;

  return (
    <Card>
      {/* Accordion Header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors rounded-t-lg"
        aria-expanded={expanded}
        aria-controls={`entries-${userName}`}
      >
        <div className="flex items-center gap-3">
          {/* Select all for this user */}
          <input
            type="checkbox"
            checked={allUserSelected}
            ref={(el) => {
              if (el) el.indeterminate = someUserSelected;
            }}
            onChange={(e) => {
              e.stopPropagation();
              onToggleSelectUser(entries.map((en) => en.id));
            }}
            onClick={(e) => e.stopPropagation()}
            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
            aria-label={`Select all entries for ${userName}`}
          />
          <div className="text-left">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{userName}</span>
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
              {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right text-xs text-gray-500 dark:text-gray-400">
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {formatDuration(totalMinutes)}
            </span>
            {' total'}
            {billableMinutes > 0 && (
              <span className="ml-2">({formatDuration(billableMinutes)} billable)</span>
            )}
          </div>
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Accordion Content */}
      {expanded && (
        <CardContent id={`entries-${userName}`}>
          <table className="w-full text-sm" aria-label={`Time entries for ${userName}`}>
            <thead>
              <tr className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase">
                <th className="py-2 px-2 w-8"></th>
                <th className="py-2 px-2">Date</th>
                <th className="py-2 px-2">Entity</th>
                <th className="py-2 px-2">Job</th>
                <th className="py-2 px-2">Description</th>
                <th className="py-2 px-2 text-right">Duration</th>
                <th className="py-2 px-2 text-center">Billable</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr
                  key={entry.id}
                  className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                >
                  <td className="py-2 px-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(entry.id)}
                      onChange={() => onToggleSelect(entry.id)}
                      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                      aria-label={`Select entry for ${entry.jobTitle} on ${entry.entryDate}`}
                    />
                  </td>
                  <td className="py-2 px-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {formatDate(entry.entryDate)}
                  </td>
                  <td className="py-2 px-2 text-gray-700 dark:text-gray-300 text-xs">
                    {entry.practiceEntityName || '—'}
                  </td>
                  <td className="py-2 px-2">
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {entry.jobTitle}
                    </div>
                    {entry.clientName && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {entry.clientName}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-2 text-gray-600 dark:text-gray-400 max-w-[200px] truncate">
                    {entry.description || '—'}
                  </td>
                  <td className="py-2 px-2 text-right font-medium text-gray-900 dark:text-gray-100">
                    {formatDuration(entry.durationMinutes)}
                  </td>
                  <td className="py-2 px-2 text-center">
                    {entry.isBillable ? (
                      <svg
                        className="w-4 h-4 mx-auto text-green-600 dark:text-green-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2.5}
                        stroke="currentColor"
                        aria-label="Billable"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4.5 12.75l6 6 9-13.5"
                        />
                      </svg>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      )}
    </Card>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function TimesheetApprovalPage() {
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [rejectComment, setRejectComment] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { data: entries, isLoading } = usePendingTimeEntries();
  const approveEntries = useApproveEntries();
  const rejectEntries = useRejectEntries();
  const { toast } = useToast();

  // Filter entries by search query (matches employee name, job title, client name, or entity)
  const filteredEntries = useMemo(() => {
    if (!entries) return [];
    if (!searchQuery.trim()) return entries;

    const q = searchQuery.toLowerCase();
    return entries.filter(
      (e) =>
        e.userName.toLowerCase().includes(q) ||
        e.jobTitle.toLowerCase().includes(q) ||
        e.clientName.toLowerCase().includes(q) ||
        e.practiceEntityName.toLowerCase().includes(q) ||
        e.description?.toLowerCase().includes(q),
    );
  }, [entries, searchQuery]);

  // Group filtered entries by user
  const groupedByUser = useMemo(() => {
    const map = new Map<
      string,
      { userName: string; entries: PendingEntry[]; totalMinutes: number; billableMinutes: number }
    >();

    for (const entry of filteredEntries) {
      if (!map.has(entry.userId)) {
        map.set(entry.userId, {
          userName: entry.userName,
          entries: [],
          totalMinutes: 0,
          billableMinutes: 0,
        });
      }
      const group = map.get(entry.userId)!;
      group.entries.push(entry);
      group.totalMinutes += entry.durationMinutes;
      if (entry.isBillable) group.billableMinutes += entry.durationMinutes;
    }

    return Array.from(map.values());
  }, [filteredEntries]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectUser = useCallback((entryIds: string[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = entryIds.every((id) => next.has(id));
      if (allSelected) {
        entryIds.forEach((id) => next.delete(id));
      } else {
        entryIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === filteredEntries.length && filteredEntries.length > 0) return new Set();
      return new Set(filteredEntries.map((e) => e.id));
    });
  }, [filteredEntries]);

  const handleApprove = () => {
    if (selectedIds.size === 0) {
      toast({ variant: 'warning', title: 'Select entries to approve.' });
      return;
    }

    approveEntries.mutate(Array.from(selectedIds), {
      onSuccess: () => {
        toast({ variant: 'success', title: `${selectedIds.size} entries approved.` });
        setSelectedIds(new Set());
      },
      onError: (error: any) => {
        toast({
          variant: 'error',
          title: error?.response?.data?.message || 'Failed to approve entries.',
        });
      },
    });
  };

  const handleReject = () => {
    if (selectedIds.size === 0) {
      toast({ variant: 'warning', title: 'Select entries to reject.' });
      return;
    }
    setShowRejectModal(true);
  };

  const confirmReject = () => {
    if (!rejectComment.trim()) {
      toast({ variant: 'warning', title: 'Please provide a rejection reason.' });
      return;
    }

    rejectEntries.mutate(
      { timeEntryIds: Array.from(selectedIds), comment: rejectComment.trim() },
      {
        onSuccess: () => {
          toast({ variant: 'success', title: `${selectedIds.size} entries rejected.` });
          setSelectedIds(new Set());
          setRejectComment('');
          setShowRejectModal(false);
        },
        onError: (error: any) => {
          toast({
            variant: 'error',
            title: error?.response?.data?.message || 'Failed to reject entries.',
          });
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/time-tracking')}
            className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            aria-label="Back to timesheet"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
            Back
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Timesheet Approvals
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Review and approve team members' time entries
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {selectedIds.size} selected
          </span>
          <Button
            variant="outline"
            onClick={handleReject}
            disabled={selectedIds.size === 0 || rejectEntries.isPending}
          >
            Reject
          </Button>
          <Button
            variant="primary"
            onClick={handleApprove}
            disabled={selectedIds.size === 0 || approveEntries.isPending}
          >
            {approveEntries.isPending ? 'Approving...' : 'Approve'}
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
          />
        </svg>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by employee, job, or client..."
          className="input-field w-full pl-10"
          aria-label="Search time entries"
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : groupedByUser.length === 0 ? (
        <Card>
          <CardContent>
            <div className="py-12 text-center text-gray-500 dark:text-gray-400">
              <p className="text-lg font-medium">
                {searchQuery ? 'No matching entries' : 'No pending approvals'}
              </p>
              <p className="text-sm mt-1">
                {searchQuery
                  ? 'Try adjusting your search terms.'
                  : 'All time entries have been reviewed.'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {/* Select all */}
          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={filteredEntries.length > 0 && selectedIds.size === filteredEntries.length}
                onChange={toggleSelectAll}
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                aria-label="Select all entries"
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Select all ({filteredEntries.length} entries)
              </span>
            </div>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {groupedByUser.length} {groupedByUser.length === 1 ? 'employee' : 'employees'}
            </span>
          </div>

          {/* Employee Accordions */}
          {groupedByUser.map(
            ({ userName, entries: userEntries, totalMinutes, billableMinutes }) => (
              <EmployeeAccordion
                key={userName}
                userName={userName}
                entries={userEntries}
                totalMinutes={totalMinutes}
                billableMinutes={billableMinutes}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleSelectUser={toggleSelectUser}
              />
            ),
          )}
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="card w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Reject Time Entries
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Rejecting {selectedIds.size} {selectedIds.size === 1 ? 'entry' : 'entries'}. Please
              provide a reason:
            </p>
            <textarea
              className="input-field w-full"
              rows={3}
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              placeholder="Reason for rejection..."
              autoFocus
            />
            <div className="flex justify-end gap-3 mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectComment('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={confirmReject}
                disabled={!rejectComment.trim() || rejectEntries.isPending}
              >
                {rejectEntries.isPending ? 'Rejecting...' : 'Confirm Reject'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
